<template>
    <div class="edit-table-wrapper" :class="{ 'teleported-wrapper': props.isTeleport }">
        <div class="flex-box-wrapper">
            <div class="edit-table-header" :class="{ 'is-teleport': props.isTeleport }">
                <h4>Edit/sort columns</h4>
                <info-icon-with-tooltip
                    tooltip-text="Use arrow buttons to reorder columns, click checkboxes to toggle show/hide"
                />
            </div>
            <list-box
                v-if="props.disableReorder"
                :options="props.tableHeaders"
                dataKey="id"
                :scroll-height="'150px'"
            >
                <template #option="{ option, index }">
                    <div class="list-item" :key="`column-${index}`">
                        {{ option.title}}
                        <checkbox
                            :model-value="!props.columnsToHide.includes(option.id)"
                            binary
                            @update:model-value="toggleColumn(option.id)"
                        />
                    </div>
                </template>
            </list-box>

            <order-list
                v-model="localTableHeaders"
                dataKey="id"
                @update:modelValue="updateColumnOrder"
                :scroll-height="'150px'"
                v-else
            >
                <template #option="{ option, index }">
                    <div class="list-item" :key="`column-${index}`">
                        {{ option.id }}
                        <checkbox
                            :model-value="!props.columnsToHide.includes(option.id)"
                            binary
                            @update:model-value="toggleColumn(option.id)"
                        />
                    </div>
                </template>
                <template #movetopicon>
                    <font-awesome-icon :icon="['fas', 'chevron-double-left']" />
                </template>
                <template #moveupicon>
                    <div class="centered">
                        <font-awesome-icon :icon="['fas', 'chevron-left']" />
                    </div>
                </template>
                <template #movedownicon>
                    <div class="centered">
                        <font-awesome-icon :icon="['fas', 'chevron-right']" />
                    </div>
                </template>
                <template #movebottomicon>
                    <div class="centered">
                        <font-awesome-icon :icon="['fas', 'chevron-double-right']" />
                    </div>
                </template>
            </order-list>
        </div>
        <div class="flex-box-wrapper" :class="{ 'is-teleport': props.isTeleport }">
            <div class="edit-table-units">
                <label>Table units</label>
                <select-button
                    class="light"
                    :model-value="props.tableUnitsSetting"
                    id="tableUnits"
                    :options="TABLE_UNITS_OPTIONS"
                    option-label="name"
                    option-value="id"
                    :allow-empty="false"
                    @update:model-value="updateTableUnitsSetting"
                >
                    <template #option="slotProps">
                        {{ slotProps.option.name }}
                        <info-icon-with-tooltip
                            v-if="slotProps.option.info"
                            :tooltip-text="slotProps.option.info"
                        />
                    </template>
                </select-button>
                <span><strong>Note:</strong> To filter a header, please select it on the table.</span>
            </div>
            <div class="edit-table-resets">
                <label>Table Reset Options</label>
                <div class="button-holder">
                    <prime-button class="plain-btn" v-tooltip.top="'Remove all filters from each header in the table'" @click="confirmResetHeaders">Reset headers</prime-button>
                    <prime-button class="plain-btn" v-tooltip.top="'Reset order and hidden headers'" @click="confirmResetTable">Reset table</prime-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { IIdName } from '@/interfaces/general/IIdName.ts';
import Checkbox from 'primevue/checkbox';
import OrderList from 'primevue/orderlist';
import ListBox from 'primevue/listbox';
import PrimeButton from 'primevue/button';
import SelectButton from 'primevue/selectbutton';
import { IGenericTableHeaderRow } from '@/interfaces/powerMarkets/table/IGenericTableHeaderRow.ts';
import InfoIconWithTooltip from '@/components/common/InfoIconWithTooltip.vue';
import { ref, watch } from 'vue';
import { useConfirm } from 'primevue/useconfirm';

const confirm = useConfirm();

const TABLE_UNITS_OPTIONS: Array<IIdName> = [
    { name: 'Inherit', id: 'inherit', info: 'Table will default to the site-wide setting (see Settings panel)' },
    { name: 'Show on hover', id: 'hover' },
    { name: 'In cell', id: 'cell' },
    { name: 'In header', id: 'header' },
    { name: 'Hidden', id: 'hidden' },
];

const props = defineProps<{
    tableHeaders: Array<IGenericTableHeaderRow>;
    columnsToHide: Array<string>;
    tableUnitsSetting: string;
    disableReorder?: boolean;
    isTeleport?: boolean;
}>();

const emit = defineEmits<{
    'close:modal': [];
    'toggle:column': [string];
    'reorder:columns': [Array<IGenericTableHeaderRow>];
    'update:table-units-setting': [string];
    'reset:headers': []; 'reset:table': [];
}>();

const localTableHeaders = ref<Array<IGenericTableHeaderRow>>([]);

function confirmResetHeaders (): void {
    confirm.require({
        message: 'Reset all table filters?',
        accept: () => {
            resetHeaders();
        },
    });
}

function confirmResetTable (): void {
    confirm.require({
        message: 'Reset all table columns?',
        accept: () => {
            resetTable();
        },
    });
}

function toggleColumn (column: string): void {
    emit('toggle:column', column);
}

function updateColumnOrder (newOrder: Array<IGenericTableHeaderRow>): void {
    emit('reorder:columns', newOrder);
}

function updateTableUnitsSetting (value: string): void {
    emit('update:table-units-setting', value);
}

function resetHeaders (): void {
    emit('reset:headers');
}

function resetTable (): void {
    emit('reset:table');
}

watch(() => props.tableHeaders, (newHeaders) => {
    localTableHeaders.value = [...newHeaders];
}, { immediate: true });

</script>

<style lang="scss" scoped>
.edit-table-wrapper {
    display: flex;
    flex-direction: column;
    gap: var(--largeSpacing);
    padding: var(--defaultSpacing);
    background-color: var(--neutral0);
    border-radius: var(--borderRadiusOuter);

    &.teleported-wrapper {
        display: grid;
        grid-template-columns: 1fr 1fr;
        background-color: unset;

        @include responsive(sm) {
            display: flex;
        }
    }

    .flex-box-wrapper {
        display: flex;
        flex-direction: column;
        gap: var(--defaultSpacing);

        &.is-teleport {
            padding-top: var(--smallSpacing);
            gap: var(--extraLargeSpacing);
        }
    }

    .edit-table-header {
        display: flex;
        align-items: center;
        justify-content: space-between;

        &.is-teleport {
            justify-content: start;
            gap: var(--defaultSpacing);
        }

        h2 {
            margin: 0;
        }
    }

    .list-item {
        display: flex;
        gap: var(--defaultSpacing);
        justify-content: space-between;
        user-select: none;
        width: 100%;
    }

    .edit-table-units {
        display: flex;
        flex-direction: column;
        gap: var(--defaultSpacing);
    }

    .edit-table-resets {
        display: flex;
        flex-direction: column;
        gap: var(--defaultSpacing);

        .button-holder {
            display: flex;
            gap: var(--defaultSpacing);
        }
    }
}
</style>
